import React from 'react';
import { useLingui } from '@lingui/react/macro';

import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex, Paragraph } from '@rover/kibble/core';
import type { ThemeParagraphSizes } from '@rover/kibble/styles/themeTypes';
import useTheme from '@rover/kibble/themes/useTheme';
import type { SvgIcon } from '@rover/types/src/SvgIcon';

import { BannerSeverity } from './BannerSeverity.constants';
import {
  getBackgroundColorForSeverity,
  getIconFillColorForSeverity,
  getIconForSeverity,
} from './BannerSeverity.utils';

/**
 * Banner has no `.native.tsx`: it composes cross-platform core primitives and splits platform
 * props through `webProps` and `rxnProps`, so this file serves web and React Native.
 */
export type Props = {
  /** Semibold heading above the message. */
  title?: React.ReactNode;
  /** The message, wrapped in a paragraph. A link or button goes in `FooterComponent`. */
  children?: React.ReactNode;
  /** The message's tone, which sets the background color and the icon. Defaults to `info`. */
  severity?: `${BannerSeverity}`;
  /** Rendered as `data-testid` on web and `testID` on React Native. Defaults to `banner-block`. */
  testID?: string;
  /**
   * Props for the `Box` that wraps the banner. A `webProps` or `rxnProps` here replaces the
   * banner's own and removes its `status` or `alert` role.
   */
  containerProps?: React.ComponentProps<typeof Box>;
  /** Replaces the icon the severity picks. */
  icon?: SvgIcon;
  /**
   * Replaces the icon color the severity picks. Takes a color value, such as one from
   * `useTheme()`, not a token name.
   */
  iconFillColor?: string;
  /** Hides the icon. Defaults to `false`. */
  hideIcon?: boolean;
  /**
   * Shows a ✕ that calls this when pressed. Banner doesn't hide itself: remove it in the handler.
   */
  onClose?: () => void;
  /**
   * Text size of the title and message. On web an array sets one per breakpoint; React Native uses
   * its first value. Defaults to `100`.
   */
  fontSize?: ThemeParagraphSizes | ThemeParagraphSizes[];
  /** A link or button, such as a `Link`, on its own line under the message. */
  FooterComponent?: React.ReactNode;
  /**
   * Renders `children` as given instead of wrapping them in a paragraph. Supply your own
   * `Paragraph` or `Text` around the copy.
   */
  suppressDefaultTypography?: boolean;
  /** Makes the whole banner pressable. On web it gets no role and can't be reached with Tab. */
  onPress?: React.ComponentProps<typeof Box>['onPress'];
  /** Web-only props: `closeButtonRef`. */
  webProps?: BannerWebOnlyProps;
  /** React Native-only props: `closeButtonRef`. */
  rxnProps?: BannerRxnOnlyProps;
};

export type BannerWebOnlyProps = {
  /** A ref to the ✕, for moving focus to it. */
  closeButtonRef?: React.ForwardedRef<HTMLElement>;
};

export type BannerRxnOnlyProps = {
  /** A ref to the ✕, for moving focus to it. */
  closeButtonRef?: React.ForwardedRef<React.ElementType>;
};

const defaultProps = {
  severity: BannerSeverity.INFO,
};

/** A persistent inline message about the page or a task on it. */
const Banner = ({
  title,
  children,
  severity = defaultProps.severity,
  testID = 'banner-block',
  containerProps = {},
  icon = undefined,
  iconFillColor = undefined,
  hideIcon = false,
  onClose = undefined,
  fontSize = '100',
  FooterComponent = undefined,
  suppressDefaultTypography = false,
  onPress = undefined,
  webProps = {},
  rxnProps = {},
}: Props): JSX.Element | null => {
  const { t } = useLingui();
  const theme = useTheme();
  const backgroundColor = getBackgroundColorForSeverity(severity as BannerSeverity, theme);
  const Icon = icon || getIconForSeverity(severity as BannerSeverity);
  if (!title && !children && !FooterComponent) return null;
  return (
    <Box
      flexDirection="row"
      justifyContent="space-between"
      p="4x"
      alignItems="flex-start"
      aria-live="polite"
      webProps={{
        role: 'status',
      }}
      rxnProps={{
        accessibilityRole: 'alert',
      }}
      testID={testID}
      borderRadius="secondary"
      onPress={onPress}
      {...containerProps}
      sx={{
        backgroundColor,
        ...containerProps?.sx,
      }}
    >
      <Flex flex="1" flexDirection="row">
        {!hideIcon && Icon && (
          <Box pr="3x" height={24}>
            <Icon
              width={24}
              height={24}
              {...(process.env.JS_ENV_NATIVE
                ? { testID: 'banner-block-icon' }
                : { 'data-testid': 'banner-block-icon' })}
              fill={iconFillColor || getIconFillColorForSeverity(severity as BannerSeverity, theme)}
            />
          </Box>
        )}
        <Box flex="1">
          {title && (
            <Flex justifyContent="space-between">
              <Paragraph fontWeight="semibold" size={fontSize} marginBottom="1x">
                {title}
              </Paragraph>
            </Flex>
          )}
          {children &&
            (suppressDefaultTypography ? (
              children
            ) : (
              <Paragraph mb="0x" size={fontSize}>
                {children}
              </Paragraph>
            ))}
          {FooterComponent && (
            <Flex mt="2x" alignItems="flex-start">
              {FooterComponent}
            </Flex>
          )}
        </Box>
        {onClose && (
          <Box height={24} pl="1x">
            <Button
              onPress={onClose}
              icon={Close}
              size="small"
              aria-label={t`Close`}
              variant="flat"
              sx={{ color: theme.textColor.tertiary.toString(), padding: 0 }}
              webProps={{ ref: webProps.closeButtonRef }}
              rxnProps={{ ref: rxnProps.closeButtonRef }}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
};
export default Banner;
